import React, {
  useCallback, useEffect, useRef,
} from 'react'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import Form, { FormConsumer } from '@active/react-ui/Form'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import IntlPhoneInput from '@active/react-ui/IntlPhoneInput'
import Input from '@active/react-ui/Input'
import CheckboxGroup from '@active/react-ui/CheckboxGroup'
import Checkbox from '@active/react-ui/Checkbox'
import isEmpty from 'lodash/isEmpty'
import { Locale, withI18n } from '@active/react-utils/i18n'
import { nameValidatorBuilder } from '../../shared/validator'
import pubSubInstance from '../../shared/utils/pubSub'
import { SHIPPING_ADDRESS_SUB_EVENT } from './utils'
import ControlledAddressWidget from './ControlledAddressWidget'

const DEFAULT_ADDRESS_LAYOUT_CONFIG = {
  gap: 20,
  fields: [
    { field: 'country', span: 12 },
    { field: 'line1', span: 12 },
    { field: 'line2', span: 12 },
    {
      field: 'city', span: 12, spanMd: 4, spanLg: 4,
    },
    {
      field: 'stateProvince', span: 12, spanMd: 4, spanLg: 4,
    },
    {
      field: 'postalCode', span: 12, spanMd: 4, spanLg: 4,
    },
  ],
}

function ShippingAddressEditor(props) {
  const {
    i18n,
    addressValue,
    addressWidgetRef,
    onAddressChange,
    onAddressReady,
    layoutConfig = DEFAULT_ADDRESS_LAYOUT_CONFIG,
    apihost,
    addressWidgetToken,
    locale,
    addressWidgetEntry: spaEntry,
    disablePrediction,
    fieldsConfig,
    required = true,
  } = props

  const nameValidate = useCallback(value => {
    const errMsg = nameValidatorBuilder(i18n, 'passport.error.validation.firstNamePrompt')(value)
    return new Promise(resolve => {
      errMsg ? resolve(errMsg) : resolve()
    })
  }, [i18n])

  return (
    <Grid rowGap={20} container={false}>
      <GridItem span={12} spanMd={6}>
        <FormGroup key="firstName" name="firstName" required withoutLayout>
          <FormLabel htmlFor="firstName" truncate>
            <Locale id="passport.common.label.fname" />
          </FormLabel>
          <FormField
            as={Input}
            validate={nameValidate}
          />
        </FormGroup>
      </GridItem>
      <GridItem span={12} spanMd={6}>
        <FormGroup key="lastName" name="lastName" required withoutLayout>
          <FormLabel htmlFor="lastName" truncate>
            <Locale id="passport.common.label.lname" />
          </FormLabel>
          <FormField
            as={Input}
            validate={nameValidate}
          />
        </FormGroup>
      </GridItem>
      <GridItem span={12}>
        <ControlledAddressWidget
          ref={addressWidgetRef}
          value={addressValue}
          onChange={onAddressChange}
          onReady={onAddressReady}
          apihost={apihost}
          addressWidgetToken={addressWidgetToken}
          locale={locale}
          spaEntry={spaEntry}
          disablePrediction={disablePrediction}
          fieldsConfig={fieldsConfig}
          layoutConfig={layoutConfig}
          required={required}
        />
      </GridItem>
      <GridItem span={12}>
        <FormGroup key="phoneNumber" name="phoneNumber" withoutLayout>
          <FormLabel htmlFor="phoneNumber" truncate><Locale id="passport.user.userProfile.phoneNumber" /></FormLabel>
          <FormField
            as={IntlPhoneInput}
          />
        </FormGroup>
      </GridItem>
      <GridItem span={12}>
        <FormGroup key="isDefault" name="isDefault" withoutLayout>
          <FormField as={CheckboxGroup}>
            <Checkbox value="true">
              <Locale id="passport.shipping.address.set.default.checkbox.label" />
            </Checkbox>
          </FormField>
        </FormGroup>
      </GridItem>
    </Grid>
  )
}

function AddressForm({
  initialValues,
  ...rest
}) {
  const formRef = useRef()
  const addressWidgetRef = useRef()
  // Track the latest address value outside the form
  const addressValueRef = useRef(initialValues?.address)

  const handleAddressChange = useCallback(newAddressValue => {
    addressValueRef.current = newAddressValue
  }, [])

  const handleSubmit = useCallback(() => {
    if (formRef.current) {
      return formRef.current.validateForm({ shouldTouchFields: true }).then(async res => {
        const isFormValid = isEmpty(res)
        const isAddressValid = addressWidgetRef.current
          ? await addressWidgetRef.current.validate({ shouldTouchFields: true })
          : false
        if (isFormValid && isAddressValid) {
          const { values } = formRef.current
          return {
            status: 'SUCCESS',
            from: 'shipping-address-editor',
            values: {
              ...values,
              address: addressValueRef.current,
            },
          }
        }
        return {
          status: 'FAILED',
          from: 'shipping-address-editor',
          message: 'form is invalid',
        }
      })
    }
    return {
      from: 'shipping-address-editor',
      status: 'FAILED',
      message: 'formRef.current is null',
    }
  }, [formRef.current])

  const getFormValues = useCallback(() => {
    if (formRef.current) {
      return {
        status: 'SUCCESS',
        from: 'shipping-address-editor',
        values: {
          ...formRef.current.values,
          address: addressValueRef.current,
        },
      }
    }
    return {
      status: 'FAILED',
      from: 'shipping-address-editor',
      message: 'formRef.current is null',
    }
  }, [formRef.current])

  const handleFormValidation = useCallback(async () => {
    if (formRef.current) {
      const formErrors = await formRef.current.validateForm({ shouldTouchFields: true })
      const isFormValid = isEmpty(formErrors)
      const isAddressValid = addressWidgetRef.current
        ? await addressWidgetRef.current.validate({ shouldTouchFields: true })
        : false
      return {
        status: isFormValid && isAddressValid ? 'SUCCESS' : 'FAILED',
        from: 'shipping-address-editor',
      }
    }
    return {
      status: 'SUCCESS',
    }
  }, [formRef.current])

  useEffect(() => {
    pubSubInstance.subscribe(SHIPPING_ADDRESS_SUB_EVENT.submit, handleSubmit)
    return () => {
      pubSubInstance.unsubscribe(SHIPPING_ADDRESS_SUB_EVENT.submit, handleSubmit)
    }
  }, [handleSubmit])

  useEffect(() => {
    pubSubInstance.subscribe(SHIPPING_ADDRESS_SUB_EVENT.getAddress, getFormValues)
    return () => {
      pubSubInstance.unsubscribe(SHIPPING_ADDRESS_SUB_EVENT.getAddress, getFormValues)
    }
  }, [getFormValues])

  useEffect(() => {
    pubSubInstance.subscribe(SHIPPING_ADDRESS_SUB_EVENT.validate, handleFormValidation)
    return () => {
      pubSubInstance.unsubscribe(SHIPPING_ADDRESS_SUB_EVENT.validate, handleFormValidation)
    }
  }, [handleFormValidation])

  const { address: initialAddress, ...formInitialValues } = initialValues || {}

  return (
    <Form trimInput initialValues={formInitialValues}>
      <FormConsumer>
        {({ form }) => {
          formRef.current = form
          return (
            <ShippingAddressEditor
              addressValue={initialAddress}
              addressWidgetRef={addressWidgetRef}
              onAddressChange={handleAddressChange}
              {...rest}
            />
          )
        }}
      </FormConsumer>
    </Form>
  )
}

export default withI18n(AddressForm)
